import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Send, Menu, X, ChevronRight, ChevronDown, Globe, Mic, MicOff, Paperclip, Calculator, Download, FileText, FileDown, Command, ImageIcon, Pill, Plus, Star, Sparkles, Gift, Crown, HeartPulse, Bookmark, BookmarkCheck, Stamp, Square, Stethoscope, Leaf, Home } from 'lucide-react';
import { Sidebar } from '../components/layout/Sidebar';
import { MessageBubble, TypingIndicator } from '../components/chat/MessageBubble';
import { MODE_CATEGORIES, getTemplatesForMode, getAllModes, getRandomHistoryFact } from '../constants/modeCategories';
import { LANGUAGES } from '../constants/modes';
import { ScrollArea } from '../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import CalculatorModal from '../components/calculators/CalculatorModal';
import DrugReferenceModal from '../components/chat/DrugReferenceModal';
import FavouritesPanel from '../components/chat/FavouritesPanel';
import { API, getUserPhone } from '../lib/api';
import InviteModal from '../components/InviteModal';
import UpgradeModal from '../components/UpgradeModal';
import { DownloadAppButton } from '../components/DownloadAppButton';
import LetterheadModal, { getStoredLetterhead } from '../components/LetterheadModal';

// Helper: format messages for export
function formatMessagesForExport(messages, sessionTitle, modeName) {
  const date = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  let html = `
    <div style="font-family:'Segoe UI',Arial,sans-serif;max-width:800px;margin:0 auto;padding:40px;color:#1e293b;">
      <div style="border-bottom:2px solid #9D174D;padding-bottom:16px;margin-bottom:24px;">
        <h1 style="font-size:22px;margin:0 0 4px;color:#0f172a;">GYNOVATE AI/SI&trade; &mdash; Clinical Export</h1>
        <p style="font-size:13px;color:#64748b;margin:0;">Mode: <strong>${modeName || 'General Query'}</strong> &nbsp;|&nbsp; ${date}</p>
        ${sessionTitle ? `<p style="font-size:13px;color:#64748b;margin:4px 0 0;">Session: ${sessionTitle}</p>` : ''}
      </div>`;
  messages.forEach(msg => {
    if (msg.role === 'user') {
      html += `<div style="background:#f8fafc;border-left:3px solid #9D174D;padding:12px 16px;margin:16px 0;border-radius:0 8px 8px 0;">
        <p style="font-size:11px;color:#9D174D;font-weight:600;margin:0 0 6px;">YOU</p>
        <p style="font-size:14px;color:#1e293b;margin:0;white-space:pre-wrap;">${(msg.content || '').replace(/</g,'&lt;').replace(/>/g,'&gt;')}</p>
      </div>`;
    } else {
      html += `<div style="padding:12px 0;margin:16px 0;border-bottom:1px solid #e2e8f0;">
        <p style="font-size:11px;color:#0f172a;font-weight:700;margin:0 0 8px;">GYNOVATE AI/SI&trade;</p>
        <div style="font-size:14px;color:#334155;line-height:1.7;white-space:pre-wrap;">${(msg.content || '').replace(/</g,'&lt;').replace(/>/g,'&gt;')}</div>
      </div>`;
    }
  });
  html += `<div style="margin-top:32px;border-top:1px solid #e2e8f0;padding-top:12px;text-align:center;">
    <p style="font-size:11px;color:#94a3b8;margin:0;">Created by Dr. Vikram Dukle, Candolim, Goa &ndash; India &nbsp;|&nbsp; GYNOVATE AI/SI&trade;</p>
    <p style="font-size:10px;color:#cbd5e1;margin:4px 0 0;">AI outputs are clinical decision support &mdash; always verify with your professional judgement.</p>
  </div></div>`;
  return html;
}

// Voice Command matching
function matchVoiceCommand(transcript) {
  const lower = transcript.toLowerCase().trim();
  // "new chat" / "new session"
  if (/^(new\s+(chat|session)|start\s+(new|fresh))/.test(lower)) return { action: 'new_chat' };
  // "go home" / "home page"
  if (/^(go\s+home|home\s*page|go\s+to\s+home|back\s+to\s+home)/.test(lower)) return { action: 'go_home' };
  // "open [mode]" / "switch to [mode]" / just the mode name
  const allModes = getAllModes();
  const modeMatch = lower.replace(/^(open|switch\s+to|go\s+to|select|start|use)\s+/, '');
  let bestMatch = null;
  let bestScore = 0;
  allModes.forEach(m => {
    const label = m.label.toLowerCase();
    if (modeMatch === label || modeMatch.includes(label) || label.includes(modeMatch)) {
      const score = modeMatch === label ? 100 : label.includes(modeMatch) ? modeMatch.length / label.length * 80 : 60;
      if (score > bestScore) { bestScore = score; bestMatch = m; }
    }
  });
  if (bestMatch && bestScore > 40) return { action: 'select_mode', mode: bestMatch };
  return null;
}

// Clean Welcome Screen - Swiss Medical Style
const QUICK_MODES = [
  { id: 'emergency', label: 'Emergency Protocols', icon: '🔴', desc: 'PPH, Eclampsia, DIC' },
  { id: 'opd', label: 'OPD Consultation', icon: '🩺', desc: 'Quick clinical advice' },
  { id: 'anc', label: 'Antenatal Care', icon: '🤰', desc: 'ANC protocols & care' },
  { id: 'discharge', label: 'Discharge Summary', icon: '📋', desc: 'Print-ready summaries' },
  { id: 'prescription', label: 'Prescription Builder', icon: '💊', desc: 'Drug doses & plans' },
  { id: 'labour', label: 'Labour Management', icon: '👶', desc: 'Induction, partograph' },
  { id: 'infertility', label: 'Infertility', icon: '🔬', desc: 'IUI, IVF, PCOS' },
  { id: 'surgery', label: 'Laparoscopy Report', icon: '✂️', desc: 'OT notes & reports' },
  { id: 'prepare-talk', label: 'Prepare a Talk', icon: '🎤', desc: 'CME · Conference scripts' },
  { id: 'patient-comm', label: 'Patient Communication', icon: '💬', desc: 'Counselling · WhatsApp · Bad-news' },
  { id: 'consent', label: 'Consent Forms', icon: '📝', desc: 'MTP, surgery, anaesthesia' },
];

const WelcomeScreen = ({ onModeSelect, onCategorySelect, dailyPearl }) => {
  const [expandedCategory, setExpandedCategory] = useState(null);

  return (
    <div className="flex-1 overflow-y-auto py-8 px-6" style={{ backgroundColor: '#FDFCF8' }}>
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mb-2">
            What can I help you with?
          </h1>
          <p className="text-base text-slate-500">
            Tap a mode below or start typing your clinical query.
          </p>
        </div>

        {/* Daily Clinical Pearl */}
        {dailyPearl && (
          <div className="mb-8 px-5 py-4 rounded-xl bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200" data-testid="daily-pearl">
            <div className="flex items-start gap-3">
              <Sparkles className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-bold text-amber-700 uppercase tracking-wider mb-1">Clinical Pearl of the Day</p>
                <p className="text-sm text-slate-700 leading-relaxed">{dailyPearl}</p>
              </div>
            </div>
          </div>
        )}

        {/* Quick-Tap Mode Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8" data-testid="quick-mode-cards">
          {QUICK_MODES.map(mode => (
            <button
              key={mode.id}
              onClick={() => {
                const cat = MODE_CATEGORIES.find(c => c.modes.some(m => m.label === mode.label)) || MODE_CATEGORIES[0];
                const modeObj = cat.modes.find(m => m.label === mode.label) || { id: mode.id, label: mode.label };
                onModeSelect(modeObj, cat);
              }}
              className="flex flex-col items-center text-center p-4 rounded-xl bg-white border border-slate-200 hover:border-slate-400 hover:shadow-sm transition-all group"
              data-testid={`quick-mode-${mode.id}`}
            >
              <span className="text-2xl mb-2">{mode.icon}</span>
              <span className="text-xs font-semibold text-slate-800 group-hover:text-slate-900">{mode.label}</span>
              <span className="text-xs text-slate-400 mt-0.5">{mode.desc}</span>
            </button>
          ))}
        </div>

        {/* All Categories (collapsible) */}
        <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-3">All Categories</p>
        <div className="space-y-2">
          {MODE_CATEGORIES.map((category) => (
            <div 
              key={category.id}
              className="bg-white border border-slate-200 rounded-lg overflow-hidden transition-all duration-200 hover:border-slate-300"
            >
              <button
                onClick={() => {
                  const isExpanding = expandedCategory !== category.id;
                  setExpandedCategory(isExpanding ? category.id : null);
                  // When expanding a category, auto-select its first mode → templates refresh immediately
                  if (isExpanding && category.modes && category.modes.length > 0) {
                    onModeSelect(category.modes[0], category);
                  }
                }}
                className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-slate-50 transition-colors"
                data-testid={`category-${category.id}`}
              >
                <div>
                  <h2 className="text-sm font-semibold text-slate-900">{category.name}</h2>
                  <p className="text-xs text-slate-400 mt-0.5">{category.description}</p>
                </div>
                <ChevronDown 
                  className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${
                    expandedCategory === category.id ? 'rotate-180' : ''
                  }`}
                />
              </button>
              {expandedCategory === category.id && (
                <div className="px-5 pb-4 border-t border-slate-100">
                  <div className="grid grid-cols-2 gap-2 pt-3">
                    {category.modes.map((mode) => (
                      <button
                        key={mode.id}
                        onClick={() => onModeSelect(mode, category)}
                        className="text-left px-3 py-2.5 rounded-md text-xs font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors"
                        data-testid={`mode-${mode.id}`}
                      >
                        {mode.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Popular Quick Queries */}
        <div className="mt-8">
          <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-3">Quick queries</p>
          <div className="flex flex-wrap gap-2">
            {[
              'PPH protocol',
              'PCOS management',
              'Eclampsia management',
              'ANC first trimester',
              'MgSO4 dosing'
            ].map((query, idx) => (
              <button
                key={idx}
                onClick={() => onModeSelect({ id: 'general', label: 'General Query' }, MODE_CATEGORIES[0], query)}
                className="px-3 py-1.5 text-xs text-slate-600 bg-white border border-slate-200 rounded-full hover:border-slate-400 hover:text-slate-900 transition-all"
                data-testid={`quick-query-${idx}`}
              >
                {query}
              </button>
            ))}
          </div>
        </div>

        <p className="text-center text-xs text-slate-400 mt-10">
          AI responses are clinical decision support — always verify with your judgement.
        </p>
        <div className="mt-3 flex items-center justify-center gap-3 text-xs text-slate-400">
          <a href="/privacy" className="hover:text-slate-600 underline">Privacy Policy</a>
          <span>|</span>
          <a href="/terms" className="hover:text-slate-600 underline">Terms of Service</a>
        </div>
      </div>
    </div>
  );
};

// Main Chat Page Component
export default function ChatPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  
  // State
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loadingSession, setLoadingSession] = useState(false);
  const [selectedMode, setSelectedMode] = useState('General Query');
  const [selectedCategory, setSelectedCategory] = useState('Clinical');
  const [language, setLanguage] = useState('English');
  const [isRecording, setIsRecording] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [calculatorOpen, setCalculatorOpen] = useState(false);
  const [showModeSelector, setShowModeSelector] = useState(false);
  const [initialQueryProcessed, setInitialQueryProcessed] = useState(false);
  const [lastAiMessageId, setLastAiMessageId] = useState(null);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [voiceCommandActive, setVoiceCommandActive] = useState(false);
  const [chatSaved, setChatSaved] = useState(false);

  const handleSaveChat = async () => {
    if (!currentSession?.id) {
      toast.error('Start a chat first');
      return;
    }
    if (messages.length === 0) {
      toast.error('No messages to save yet');
      return;
    }
    try {
      const BACKEND = API;
      await fetch(`${BACKEND}/api/sessions/${currentSession.id}/save`, { method: 'PATCH' });
      setChatSaved(true);
      toast.success('Chat saved — it will appear in your sidebar');
    } catch (e) {
      toast.error('Could not save chat. Try again.');
    }
  };

  // Reset saved flag when switching sessions
  useEffect(() => {
    setChatSaved(!!currentSession?.is_saved);
  }, [currentSession?.id, currentSession?.is_saved]);
  const [drugRefOpen, setDrugRefOpen] = useState(false);
  const [mobileToolsOpen, setMobileToolsOpen] = useState(false);
  const [favouritesOpen, setFavouritesOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [letterheadOpen, setLetterheadOpen] = useState(false);
  const [letterheadSet, setLetterheadSet] = useState(!!getStoredLetterhead());

  // Sync letterhead from backend if not stored locally (saved once, works everywhere)
  useEffect(() => {
    if (getStoredLetterhead()) return;
    const phone = getUserPhone();
    if (!phone) return;
    fetch(`${API}/api/letterhead/${encodeURIComponent(phone)}`)
      .then((r) => r.json())
      .then((d) => {
        if (d.letterhead && Object.values(d.letterhead).some((v) => (v || '').toString().trim())) {
          localStorage.setItem('gynovate_letterhead', JSON.stringify(d.letterhead));
          setLetterheadSet(true);
        }
      })
      .catch(() => {});
  }, []);
  const [planActive, setPlanActive] = useState(true); // default true to avoid flash; corrected after fetch
  const [planLoaded, setPlanLoaded] = useState(false);
  const [planInfo, setPlanInfo] = useState({ access: 'trial', seconds_left: null, trial_expires_at: null });

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('gynovate_signup') || 'null');
      if (!saved?.phone) { setPlanLoaded(true); return; }
      const BACKEND = API;
      fetch(`${BACKEND}/api/plan/${saved.phone}`)
        .then(r => r.json())
        .then(d => {
          setPlanActive(!!d.active);
          setPlanInfo({
            access: d.access || (d.active ? 'trial' : 'expired'),
            seconds_left: d.seconds_left ?? null,
            trial_expires_at: d.trial_expires_at ?? null,
          });
          setPlanLoaded(true);
          // Launch offer: app is free during early access — never auto-open upgrade modal.
          setUpgradeOpen(false);
        })
        .catch(() => setPlanLoaded(true));
    } catch (e) { setPlanLoaded(true); }
  }, [upgradeOpen]); // refetch after closing upgrade modal (in case they upgraded)
  const [dailyPearl, setDailyPearl] = useState(null);
  const voiceCommandRef = useRef(null);  // Refs
  const messagesEndRef = useRef(null);
  const aiMessageStartRef = useRef(null);
  const textareaRef = useRef(null);
  const recognitionRef = useRef(null);
  const fileInputRef = useRef(null);
  const abortControllerRef = useRef(null);

  // Fetch sessions
  const fetchSessions = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/sessions`, { params: { phone: getUserPhone() } });
      setSessions(res.data);
    } catch (err) {
      console.error('Failed to fetch sessions:', err);
    }
  }, []);

  // Load session
  const loadSession = useCallback(async (id) => {
    if (!id) return;
    setLoadingSession(true);
    try {
      const res = await axios.get(`${API}/api/sessions/${id}`);
      setCurrentSession(res.data);
      setMessages(res.data.messages || []);
      setSelectedMode(res.data.mode || 'General Query');
    } catch (err) {
      toast.error('Failed to load session');
    } finally {
      setLoadingSession(false);
    }
  }, []);

  // Effects
  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  // Fetch daily clinical pearl
  useEffect(() => {
    axios.get(`${API}/api/daily-pearl`).then(res => setDailyPearl(res.data.pearl)).catch(() => {});
  }, []);

  useEffect(() => {
    if (sessionId) {
      loadSession(sessionId);
    } else {
      setCurrentSession(null);
      setMessages([]);
    }
  }, [sessionId, loadSession]);

  // Handle mode from URL and create session if needed
  useEffect(() => {
    if (sessionId) return;
    
    const urlParams = new URLSearchParams(window.location.search);
    const initialQuery = urlParams.get('q');
    const initialLang = urlParams.get('lang');
    const initialMode = urlParams.get('mode');
    
    // Set mode if provided in URL
    if (initialMode && !initialQueryProcessed) {
      setSelectedMode(initialMode);
      
      // If only mode is provided (no query), create a session for that mode
      if (!initialQuery) {
        setInitialQueryProcessed(true);
        const createModeSession = async () => {
          try {
            const res = await axios.post(`${API}/api/sessions`, {
              mode: initialMode,
              title: `New ${initialMode} Session`,
              phone: getUserPhone(),
            });
            const newSession = res.data;
            setSessions(prev => [newSession, ...prev]);
            setCurrentSession(newSession);
            setMessages([]);
            navigate(`/chat/${newSession.id}`, { replace: true });
            
            setTimeout(() => {
              textareaRef.current?.focus();
            }, 100);
          } catch (err) {
            console.error('Failed to create mode session:', err);
          }
        };
        createModeSession();
        return;
      }
    }
    
    if (initialQuery && !initialQueryProcessed) {
      setInitialQueryProcessed(true);
      if (initialLang) setLanguage(initialLang);
      
      const autoSendMessage = async () => {
        try {
          const modeToUse = initialMode || selectedMode;
          const res = await axios.post(`${API}/api/sessions`, {
            mode: modeToUse,
            title: initialQuery.slice(0, 50) + (initialQuery.length > 50 ? '...' : ''),
            phone: getUserPhone(),
          });
          const newSession = res.data;
          setSessions(prev => [newSession, ...prev]);
          setCurrentSession(newSession);
          
          const userMsg = {
            id: `u-${Date.now()}`,
            role: 'user',
            content: initialQuery,
            mode: modeToUse,
            timestamp: new Date().toISOString(),
          };
          
          const streamingMsgId = `ai-stream-${Date.now()}`;
          const streamingMsg = {
            id: streamingMsgId,
            role: 'assistant',
            content: '',
            mode: modeToUse,
            timestamp: new Date().toISOString(),
            isStreaming: true,
          };
          
          setMessages([userMsg, streamingMsg]);
          setIsLoading(true);
          window.history.replaceState({}, '', `/chat/${newSession.id}`);

          const controller = new AbortController();
          abortControllerRef.current = controller;
          const response = await fetch(`${API}/api/sessions/${newSession.id}/chat/stream`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              message: initialQuery,
              mode: modeToUse,
              language: initialLang || language,
              letterhead: getStoredLetterhead() || undefined,
            }),
            signal: controller.signal,
          });

          if (!response.ok) throw new Error('Stream error');

          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let fullContent = '';
          let finalAiMessage = null;
          let pendingChunks = '';
          let rafId = null;

          const flushChunks = () => {
            if (pendingChunks) {
              fullContent += pendingChunks;
              pendingChunks = '';
              const snapshot = fullContent;
              setMessages(prev => prev.map(m =>
                m.id === streamingMsgId ? { ...m, content: snapshot } : m
              ));
            }
            rafId = null;
          };

          let sseBuf = '';
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            sseBuf += decoder.decode(value, { stream: true });
            const lines = sseBuf.split('\n');
            sseBuf = lines.pop() || '';

            for (const line of lines) {
              if (line.startsWith('data: ')) {
                const data = line.slice(6);
                if (data === '[DONE]') continue;
                
                try {
                  const parsed = JSON.parse(data);
                  if (parsed.type === 'chunk') {
                    pendingChunks += parsed.content;
                    if (!rafId) {
                      rafId = requestAnimationFrame(flushChunks);
                    }
                  } else if (parsed.type === 'complete') {
                    finalAiMessage = parsed.data;
                  }
                } catch (e) {}
              }
            }
          }

          if (pendingChunks) {
            fullContent += pendingChunks;
            pendingChunks = '';
          }
          if (rafId) cancelAnimationFrame(rafId);

          if (finalAiMessage) {
            setMessages(prev => prev.map(m =>
              m.id === streamingMsgId ? { ...finalAiMessage, isStreaming: false } : m
            ));
          }
          
          fetchSessions();
        } catch (err) {
          if (err?.name === 'AbortError') {
            setMessages(prev => prev.map(m => (m.isStreaming ? { ...m, isStreaming: false } : m)));
            toast.info('Response stopped');
          } else {
            console.error('Auto send error:', err);
            toast.error('Failed to send message');
          }
        } finally {
          abortControllerRef.current = null;
          setIsLoading(false);
        }
      };
      
      autoSendMessage();
    }
  }, [sessionId, initialQueryProcessed, selectedMode, language, fetchSessions]);

  // Auto-scroll
  useEffect(() => {
    const lastAiMsg = [...messages].reverse().find(m => m.role === 'assistant');
    
    if (lastAiMsg && !lastAiMsg.isStreaming && lastAiMsg.id !== lastAiMessageId) {
      setLastAiMessageId(lastAiMsg.id);
      setTimeout(() => {
        aiMessageStartRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } else if (isLoading) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isLoading, lastAiMessageId]);

  // Latest Evidence — live PubMed stream appended as a new assistant message
  const fetchLatestEvidence = async (aiIdx) => {
    let topic = '';
    for (let i = aiIdx; i >= 0; i--) {
      if (messages[i].role === 'user') { topic = messages[i].content; break; }
    }
    if (!topic) topic = messages[aiIdx]?.content?.slice(0, 200) || '';
    const evId = `ev-${Date.now()}`;
    setMessages(prev => [...prev, { id: evId, role: 'assistant', content: '', mode: 'Latest Evidence 2026', timestamp: new Date().toISOString(), isStreaming: true }]);
    try {
      const response = await fetch(`${API}/api/evidence/latest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ topic, language }),
      });
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      let full = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split('\n');
        buf = lines.pop();
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const d = line.slice(6).trim();
          if (d === '[DONE]') continue;
          try {
            const p = JSON.parse(d);
            if (p.type === 'chunk') {
              full += p.content;
              const snap = full;
              setMessages(prev => prev.map(m => (m.id === evId ? { ...m, content: snap } : m)));
            } else if (p.type === 'complete' && p.data?.content) {
              full = p.data.content;
            }
          } catch { /* partial json */ }
        }
      }
      setMessages(prev => prev.map(m => (m.id === evId ? { ...m, content: full, isStreaming: false } : m)));
    } catch {
      setMessages(prev => prev.map(m => (m.id === evId ? { ...m, content: 'Could not fetch latest evidence — please try again.', isStreaming: false } : m)));
    }
  };

  // Voice input
  const startVoiceInput = () => {
    if (!('webkitSpeechRecognition' in window || 'SpeechRecognition' in window)) {
      toast.error('Voice input not supported in this browser');
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.lang = language === 'English' ? 'en-IN' : 'hi-IN';
    recognition.continuous = true;
    recognition.interimResults = true;

    const base = inputText ? inputText.trim() + ' ' : '';
    let finalText = '';

    recognition.onresult = (event) => {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const t = event.results[i][0].transcript;
        if (event.results[i].isFinal) finalText += t.trim() + ' ';
        else interim += t;
      }
      setInputText((base + finalText + interim).replace(/\s+/g, ' ').trimStart());
    };

    recognition.onerror = (event) => {
      setIsRecording(false);
      if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
        toast.error('Microphone blocked — allow mic access in browser settings');
      } else if (event.error !== 'no-speech' && event.error !== 'aborted') {
        toast.error('Voice recognition failed');
      }
    };

    recognition.onend = () => setIsRecording(false);

    recognitionRef.current = recognition;
    recognition.start();
    setIsRecording(true);
  };

  const stopVoiceInput = () => {
    recognitionRef.current?.stop();
    setIsRecording(false);
  };

  // File handling
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('File too large. Maximum 10MB allowed.');
      return;
    }

    setSelectedFile(file);
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setFilePreview(e.target?.result);
      reader.readAsDataURL(file);
    } else {
      setFilePreview(null);
    }
  };

  const clearSelectedFile = () => {
    setSelectedFile(null);
    setFilePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Create new session
  const createNewSession = async () => {
    try {
      const res = await axios.post(`${API}/api/sessions`, {
        mode: selectedMode,
        title: `New ${selectedMode} Session`,
        phone: getUserPhone(),
      });
      setSessions(prev => [res.data, ...prev]);
      setCurrentSession(res.data);
      setMessages([]);
      navigate(`/chat/${res.data.id}`);
    } catch (err) {
      toast.error('Failed to create session');
    }
  };

  // Send message
  const stopGeneration = () => {
    if (abortControllerRef.current) {
      try { abortControllerRef.current.abort(); } catch { /* noop */ }
    }
  };

  const sendMessage = async () => {
    const messageText = inputText.trim();
    if ((!messageText && !selectedFile) || isLoading) return;

    // Launch offer: app is free during early access — no trial gate.

    let session = currentSession;
    if (!session) {
      try {
        const res = await axios.post(`${API}/api/sessions`, {
          mode: selectedMode,
          title: messageText.slice(0, 50) + (messageText.length > 50 ? '...' : ''),
          phone: getUserPhone(),
        });
        session = res.data;
        setSessions(prev => [session, ...prev]);
        setCurrentSession(session);
        window.history.replaceState({}, '', `/chat/${session.id}`);
      } catch (err) {
        toast.error('Failed to create session');
        return;
      }
    }

    const optimisticId = `temp-${Date.now()}`;
    const optimisticMsg = {
      id: optimisticId,
      role: 'user',
      content: messageText,
      mode: selectedMode,
      timestamp: new Date().toISOString(),
      file: selectedFile ? { name: selectedFile.name, type: selectedFile.type } : null,
    };

    setMessages(prev => [...prev, optimisticMsg]);
    setInputText('');
    setIsLoading(true);

    try {
      // If file is selected, use upload endpoint
      if (selectedFile) {
        const formData = new FormData();
        formData.append('file', selectedFile);
        formData.append('message', messageText);
        formData.append('mode', selectedMode);
        formData.append('language', language);

        const uploadRes = await axios.post(`${API}/api/sessions/${session.id}/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        const data = uploadRes.data;
        setMessages(prev => [
          ...prev.filter(m => m.id !== optimisticId),
          data.user_message,
          data.ai_message,
        ]);
        clearSelectedFile();
        fetchSessions();
        setIsLoading(false);
        return;
      }

      // Streaming chat (no file)
      const streamingMsgId = `ai-stream-${Date.now()}`;
      const streamingMsg = {
        id: streamingMsgId,
        role: 'assistant',
        content: '',
        mode: selectedMode,
        timestamp: new Date().toISOString(),
        isStreaming: true,
      };
      
      setMessages(prev => [
        ...prev.filter(m => m.id !== optimisticId),
        { ...optimisticMsg, id: `u-${Date.now()}` },
        streamingMsg
      ]);

      const controller = new AbortController();
      abortControllerRef.current = controller;
      const response = await fetch(`${API}/api/sessions/${session.id}/chat/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: messageText,
          mode: selectedMode,
          language,
          letterhead: getStoredLetterhead() || undefined,
        }),
        signal: controller.signal,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Server error: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullContent = '';
      let finalAiMessage = null;
      let pendingChunks = '';
      let rafId = null;

      // Batch chunk updates via requestAnimationFrame for smooth rendering
      const flushChunks = () => {
        if (pendingChunks) {
          fullContent += pendingChunks;
          pendingChunks = '';
          const snapshot = fullContent;
          setMessages(prev => prev.map(m =>
            m.id === streamingMsgId ? { ...m, content: snapshot } : m
          ));
        }
        rafId = null;
      };

      let sseBuf = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        sseBuf += decoder.decode(value, { stream: true });
        const lines = sseBuf.split('\n');
        sseBuf = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data === '[DONE]') continue;
            
            try {
              const parsed = JSON.parse(data);
              
              if (parsed.type === 'chunk') {
                pendingChunks += parsed.content;
                if (!rafId) {
                  rafId = requestAnimationFrame(flushChunks);
                }
              } else if (parsed.type === 'complete') {
                finalAiMessage = parsed.data;
              } else if (parsed.type === 'error') {
                throw new Error(parsed.message || 'AI response error');
              }
            } catch (e) {
              if (e.message && !e.message.includes('JSON')) throw e;
            }
          }
        }
      }

      // Flush any remaining chunks
      if (pendingChunks) {
        fullContent += pendingChunks;
        pendingChunks = '';
      }
      if (rafId) cancelAnimationFrame(rafId);

      if (finalAiMessage) {
        setMessages(prev => prev.map(m =>
          m.id === streamingMsgId ? { ...finalAiMessage, isStreaming: false } : m
        ));
      } else if (fullContent) {
        setMessages(prev => prev.map(m =>
          m.id === streamingMsgId ? { ...m, content: fullContent, isStreaming: false } : m
        ));
      }

      // Reset mode after each response — every new question is independent
      setSelectedMode('General Query');

      clearSelectedFile();
      fetchSessions();
    } catch (err) {
      if (err?.name === 'AbortError') {
        // User pressed Stop — keep whatever streamed so far, mark message as not-streaming
        setMessages(prev => prev.map(m => (m.isStreaming ? { ...m, isStreaming: false } : m)));
        toast.info('Response stopped');
      } else {
        setMessages(prev => prev.filter(m => m.id !== optimisticId || m.role === 'user'));
        toast.error(err.message || 'Failed to get response');
        console.error('Chat error:', err);
      }
    } finally {
      abortControllerRef.current = null;
      setIsLoading(false);
    }
  };

  // Mode selection handler - switch mode in current chat or create new session from welcome
  const handleModeSelect = async (mode, category, initialQuery = null) => {
    setSelectedMode(mode.label);
    setSelectedCategory(category.name);
    setShowModeSelector(false);
    
    if (initialQuery) {
      setInputText(initialQuery);
      setTimeout(() => {
        sendMessage();
      }, 100);
    } else if (currentSession) {
      // Already in a chat — just switch mode, no new session
      toast.success(`Switched to ${mode.label}`, { duration: 1500 });
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 100);
    } else {
      // No active session (welcome screen) — create a new session
      try {
        const res = await axios.post(`${API}/api/sessions`, {
          mode: mode.label,
          title: `New ${mode.label} Session`,
          phone: getUserPhone(),
        });
        const newSession = res.data;
        setSessions(prev => [newSession, ...prev]);
        setCurrentSession(newSession);
        setMessages([]);
        navigate(`/chat/${newSession.id}`);
        
        setTimeout(() => {
          textareaRef.current?.focus();
        }, 100);
      } catch (err) {
        console.error('Failed to create session:', err);
        toast.error('Failed to start session');
      }
    }
  };

  // Handle key press
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Get templates for current mode
  const currentTemplates = getTemplatesForMode(
    getAllModes().find(m => m.label === selectedMode)?.id || 'general'
  );

  // Export as PDF
  // Letterhead HTML header (with logo) for client-side PDF/Word exports
  const buildLetterheadHtml = () => {
    const lh = getStoredLetterhead();
    if (!lh) return '';
    const logo = lh.logo_url ? `<img src="${API}${lh.logo_url}" style="height:60px;margin:0 auto 6px;display:block" />` : '';
    const sub = [lh.reg_no ? `Reg. No: ${lh.reg_no}` : '', lh.address || '', lh.phone ? `Ph: ${lh.phone}` : '', lh.timings || ''].filter(Boolean).join(' · ');
    return `<div style="text-align:center;border-bottom:2px solid #9D174D;padding-bottom:10px;margin-bottom:18px">
      ${logo}
      ${lh.clinic_name ? `<div style="font-size:19px;font-weight:800;color:#9D174D;letter-spacing:0.5px">${lh.clinic_name.toUpperCase()}</div>` : ''}
      ${lh.doctor_name ? `<div style="font-weight:700;font-size:14px;margin-top:2px">${lh.doctor_name}${lh.qualifications ? ', ' + lh.qualifications : ''}</div>` : ''}
      ${sub ? `<div style="font-size:11px;color:#475569;margin-top:2px">${sub}</div>` : ''}
    </div>`;
  };

  const handleExportPDF = () => {
    if (messages.length === 0) { toast.error('No messages to export'); return; }
    const html = buildLetterheadHtml() + formatMessagesForExport(messages, currentSession?.title, selectedMode);
    const win = window.open('', '_blank');
    if (!win) { toast.error('Pop-up blocked. Please allow pop-ups.'); return; }
    win.document.write(`<!DOCTYPE html><html><head><title>GYNOVATE AI/SI Export</title>
      <style>@media print{body{padding:20px}}</style></head><body>${html}</body></html>`);
    win.document.close();
    setTimeout(() => win.print(), 300);
    setShowExportMenu(false);
    toast.success('PDF export opened');
  };

  // Export as Word
  const handleExportWord = () => {
    if (messages.length === 0) { toast.error('No messages to export'); return; }
    const html = buildLetterheadHtml() + formatMessagesForExport(messages, currentSession?.title, selectedMode);
    const fullHtml = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
      <head><meta charset="utf-8"><title>GYNOVATE AI/SI Export</title></head><body>${html}</body></html>`;
    const blob = new Blob([fullHtml], { type: 'application/msword' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `gynovate_${(currentSession?.title || 'chat').replace(/[^a-z0-9]/gi,'_').slice(0,30)}.doc`;
    a.click();
    URL.revokeObjectURL(url);
    setShowExportMenu(false);
    toast.success('Word document downloaded');
  };

  // Voice Command
  const startVoiceCommand = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error('Voice commands not supported in this browser'); return; }
    const recognition = new SR();
    recognition.lang = 'en-IN';
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.onresult = (e) => {
      const transcript = e.results[0][0].transcript;
      setVoiceCommandActive(false);
      const cmd = matchVoiceCommand(transcript);
      if (!cmd) {
        toast.info(`Heard: "${transcript}" — Try "Open Emergency Protocols" or "New Chat"`);
        return;
      }
      switch (cmd.action) {
        case 'new_chat':
          createNewSession();
          toast.success('New chat created');
          break;
        case 'go_home':
          navigate('/');
          toast.success('Going home');
          break;
        case 'select_mode': {
          const cat = MODE_CATEGORIES.find(c => c.modes.some(m => m.id === cmd.mode.id));
          handleModeSelect(cmd.mode, cat || MODE_CATEGORIES[0]);
          toast.success(`Opened ${cmd.mode.label}`);
          break;
        }
        default: break;
      }
    };
    recognition.onerror = () => { setVoiceCommandActive(false); toast.error('Voice command failed'); };
    recognition.onend = () => setVoiceCommandActive(false);
    voiceCommandRef.current = recognition;
    recognition.start();
    setVoiceCommandActive(true);
    toast.info('Listening... Say a mode name or "New Chat"');
  };

  const stopVoiceCommand = () => {
    voiceCommandRef.current?.stop();
    setVoiceCommandActive(false);
  };

  return (
    <div
      className="flex h-screen relative overflow-hidden"
      style={{
        background:
          'radial-gradient(120% 80% at 0% 0%, #FFF5F6 0%, transparent 55%), radial-gradient(100% 80% at 100% 100%, #FFFAF0 0%, transparent 55%), linear-gradient(180deg, #FDFCF8 0%, #FFF8F4 100%)',
      }}
    >
      <div className="pointer-events-none fixed inset-0 z-0 opacity-[0.05] mix-blend-multiply" aria-hidden="true" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")" }} />
      {/* Sidebar */}
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        sessions={sessions}
        currentSessionId={currentSession?.id}
        onSessionSelect={(s) => {
          navigate(`/chat/${s.id}`);
          setSidebarOpen(false);
        }}
        onNewSession={createNewSession}
        selectedMode={selectedMode}
        onModeSelect={(mode) => {
          setSelectedMode(mode);
          toast.success(`Switched to ${mode}`, { duration: 1500 });
          setSidebarOpen(false);
        }}
        language={language}
        onLanguageChange={setLanguage}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header - Clean Swiss Style */}
        <header className="flex-shrink-0 bg-white/70 backdrop-blur-xl border-b border-slate-200/70 px-4 py-1.5 flex items-center flex-wrap gap-y-1">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden p-2 rounded-lg hover:bg-slate-100 text-slate-600 mr-3"
            data-testid="menu-btn"
          >
            <Menu className="w-5 h-5" />
          </button>

          {/* Current Mode Display */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowModeSelector(!showModeSelector)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-md hover:bg-slate-100 transition-colors"
            >
              <span className="text-sm font-semibold text-slate-900">{selectedMode}</span>
              <ChevronDown className="w-4 h-4 text-slate-400" />
            </button>
            {currentSession && (
              <span className="text-sm text-slate-400 truncate max-w-xs hidden sm:block">
                {currentSession.title}
              </span>
            )}
          </div>

          <div className="ml-auto flex items-center justify-end flex-wrap gap-1.5 min-w-0">
            {/* Voice Command */}
            <button
              onClick={voiceCommandActive ? stopVoiceCommand : startVoiceCommand}
              className={`px-2.5 sm:px-3 py-2 rounded-lg text-sm font-medium transition-colors flex-shrink-0 ${
                voiceCommandActive
                  ? 'bg-red-500 text-white animate-pulse'
                  : 'hover:bg-slate-100 text-slate-600'
              }`}
              data-testid="voice-command-btn"
              title="Voice command — say a mode name to navigate"
            >
              <Command className="w-4 h-4 inline mr-1" />
              {voiceCommandActive ? (
                <span className="hidden sm:inline">Listening...</span>
              ) : (
                <span className="hidden sm:inline">Voice</span>
              )}
            </button>

            {/* Export */}
            <div className="relative">
              <button
                onClick={() => setShowExportMenu(!showExportMenu)}
                className="px-3 py-2 rounded-lg hover:bg-slate-100 text-slate-600 text-sm font-medium transition-colors disabled:opacity-40"
                data-testid="export-btn"
                disabled={messages.length === 0}
                title="Export chat session"
              >
                <Download className="w-4 h-4 inline mr-1" />
                <span className="hidden sm:inline">Export</span>
              </button>
              {showExportMenu && messages.length > 0 && (
                <div className="absolute right-0 mt-1 w-48 bg-white border border-slate-200 rounded-lg shadow-lg z-[60] py-1">
                  <button
                    onClick={handleExportPDF}
                    className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                    data-testid="export-pdf-btn"
                  >
                    <FileText className="w-4 h-4 text-red-500" />
                    Export as PDF
                  </button>
                  <button
                    onClick={handleExportWord}
                    className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                    data-testid="export-word-btn"
                  >
                    <FileDown className="w-4 h-4 text-blue-500" />
                    Export as Word
                  </button>
                </div>
              )}
            </div>

            <button
              onClick={() => setLetterheadOpen(true)}
              className={`px-3 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center gap-1.5 border ${
                letterheadSet
                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
                  : 'bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100'
              }`}
              data-testid="letterhead-btn"
              title={letterheadSet ? 'Letterhead saved — auto-fills into all documents' : 'Add your clinic letterhead once — all documents will carry it'}
            >
              <Stamp className="w-4 h-4" />
              <span>{letterheadSet ? 'Letterhead ✓' : 'Letterhead'}</span>
            </button>

            <button
              onClick={() => setCalculatorOpen(true)}
              className="px-3 py-2 rounded-lg bg-sky-50 text-sky-700 border border-sky-200 hover:bg-sky-100 text-sm font-semibold transition-colors flex items-center gap-1.5"
              data-testid="calculator-btn"
            >
              <Calculator className="w-4 h-4" />
              <span>Calculators</span>
            </button>

            <button
              onClick={() => navigate('/')}
              className="px-3 py-2 rounded-lg text-sm font-semibold bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors flex items-center gap-1.5"
              data-testid="go-home-btn"
              title="Go to Gynovate main page"
            >
              <Home className="w-4 h-4" />
              <span className="hidden sm:inline">Home</span>
            </button>

            {/* force product nav onto its own line */}
            <span className="basis-full h-0" aria-hidden="true"></span>

            <button
              onClick={() => navigate('/social')}
              className="px-3 py-2 rounded-lg text-white text-sm font-bold transition-opacity hover:opacity-90 flex items-center gap-1.5 shadow-sm"
              style={{ background: 'linear-gradient(135deg, #c0123c, #7a0e2e)' }}
              data-testid="gynovate-social-btn"
              title="Gynovate Social — your AI social media manager for gynaecologists"
            >
              <img src="/gynovate-social-logo.png" alt="" className="w-4 h-4" />
              <span className="hidden sm:inline">Gynovate Social</span>
            </button>

            <button
              onClick={() => navigate('/personal')}
              className="px-3 py-2 rounded-lg text-white text-sm font-bold transition-opacity hover:opacity-90 flex items-center gap-1.5 shadow-sm"
              style={{ background: 'linear-gradient(135deg, #d97706, #b45309)' }}
              data-testid="gynovate-personal-btn"
              title="Gynovate Personal — communication, counselling, leadership & new skills"
            >
              <Sparkles className="w-4 h-4" />
              <span className="hidden sm:inline">Personal</span>
            </button>

            <button
              onClick={() => navigate('/2030')}
              className="px-3 py-2 rounded-lg text-white text-sm font-bold transition-opacity hover:opacity-90 flex items-center gap-1.5 shadow-sm"
              style={{ background: 'linear-gradient(135deg, #06b6d4, #4f46e5)' }}
              data-testid="gynovate-2030-btn"
              title="Gynovate 2030 — imagine and future-proof your practice"
            >
              🚀
              <span className="hidden sm:inline">2030</span>
            </button>

            <button
              onClick={() => navigate('/md')}
              className="px-3 py-2 rounded-lg text-white text-sm font-bold transition-opacity hover:opacity-90 flex items-center gap-1.5 shadow-sm"
              style={{ background: 'linear-gradient(135deg, #0ea5a4, #0369a1)' }}
              data-testid="gynovate-md-btn"
              title="Gynovate MD — evidence-based answers for all specialities"
            >
              <Stethoscope className="w-4 h-4" />
              <span className="hidden sm:inline">Gynovate MD</span>
            </button>

            <button
              onClick={() => navigate('/gia')}
              className="px-3 py-2 rounded-lg text-white text-sm font-bold transition-colors flex items-center gap-1.5 shadow-sm"
              style={{ background: 'linear-gradient(135deg, #a855f7, #7c3aed)' }}
              data-testid="talk-to-gia-btn"
              title="Talk to Gia — mental wellness for doctors"
            >
              <HeartPulse className="w-4 h-4" />
              <span className="hidden sm:inline">Talk to Gia</span>
            </button>

            <button
              onClick={() => navigate('/health')}
              className="px-3 py-2 rounded-lg text-white text-sm font-bold transition-opacity hover:opacity-90 flex items-center gap-1.5 shadow-sm"
              style={{ background: 'linear-gradient(135deg, #3a7d5d, #2E4A3B)' }}
              data-testid="gynovate-health-btn"
              title="Gynovate Health — your health, your dashboard"
            >
              <Leaf className="w-4 h-4" />
              <span className="hidden sm:inline">Gynovate Health</span>
            </button>

            {currentSession && messages.length > 0 && (
              <button
                onClick={handleSaveChat}
                disabled={chatSaved}
                className={`px-3 py-2 rounded-lg text-sm font-semibold transition-all flex items-center gap-1.5 ${
                  chatSaved
                    ? 'bg-emerald-50 text-emerald-700 cursor-default'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
                data-testid="save-chat-btn"
                title={chatSaved ? 'Chat saved to sidebar' : 'Save this chat (unsaved chats auto-delete in 24h)'}
              >
                {chatSaved
                  ? <><BookmarkCheck className="w-4 h-4" /><span className="hidden sm:inline">Saved</span></>
                  : <><Bookmark className="w-4 h-4" /><span className="hidden sm:inline">Save Chat</span></>
                }
              </button>
            )}

            {/* Download App — visible to logged-in doctors */}
            <DownloadAppButton variant="light" testid="chat-download-app-btn" />

            <button
              onClick={() => setInviteOpen(true)}
              className="px-3 py-2 rounded-lg hover:bg-raspberry-50 text-raspberry-600 text-sm font-semibold transition-colors"
              data-testid="invite-btn"
              title="Invite a colleague to Gynovate AI/SI"
            >
              <Gift className="w-4 h-4 inline mr-1.5" />
              <span className="hidden sm:inline">Invite</span>
            </button>

            <button
              onClick={() => setFavouritesOpen(true)}
              className="px-3 py-2 rounded-lg hover:bg-slate-100 text-slate-600 text-sm font-medium transition-colors"
              data-testid="favourites-btn"
            >
              <Star className="w-4 h-4 inline mr-1.5" />
              <span className="hidden sm:inline">Favourites</span>
            </button>
          </div>
        </header>

        {/* Trial countdown banner removed — app is free during launch */}

        {/* Mode Selector Dropdown */}
        {showModeSelector && (
          <div className="absolute top-16 left-4 lg:left-64 z-50 w-80 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden">
            <div className="p-4 border-b border-slate-100">
              <h3 className="font-semibold text-slate-900">Select Mode</h3>
            </div>
            <div className="max-h-96 overflow-y-auto">
              {MODE_CATEGORIES.map((category) => (
                <div key={category.id} className="border-b border-slate-100 last:border-0">
                  <p className="px-4 py-2 text-xs font-medium text-slate-400 uppercase tracking-wider bg-slate-50">
                    {category.name}
                  </p>
                  <div className="py-1">
                    {category.modes.map((mode) => (
                      <button
                        key={mode.id}
                        onClick={() => handleModeSelect(mode, category)}
                        className={`w-full px-4 py-2 text-left text-sm hover:bg-slate-100 transition-colors ${
                          selectedMode === mode.label ? 'bg-slate-100 text-slate-900 font-medium' : 'text-slate-600'
                        }`}
                      >
                        {mode.label}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Chat Area */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {loadingSession ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="flex gap-1.5">
                {[0, 1, 2].map(i => (
                  <span key={i} className="w-2 h-2 rounded-full bg-slate-300 animate-pulse" style={{ animationDelay: `${i * 0.15}s` }} />
                ))}
              </div>
            </div>
          ) : (messages.length > 0 || currentSession || isLoading) ? (
            <div className="flex-1 overflow-y-auto py-6 px-4" data-testid="messages-container">
              <div className="max-w-3xl mx-auto">
                {messages.map((msg, idx) => {
                  const lastAiMsgIdx = messages.map((m, i) => m.role === 'assistant' ? i : -1).filter(i => i >= 0).pop();
                  const isLastAiMessage = msg.role === 'assistant' && idx === lastAiMsgIdx;
                  
                  return (
                    <div key={msg.id} ref={isLastAiMessage ? aiMessageStartRef : null}>
                      <MessageBubble
                        message={msg}
                        onLatestEvidence={msg.role === 'assistant' && msg.content && !msg.isStreaming ? () => fetchLatestEvidence(idx) : undefined}
                      />
                    </div>
                  );
                })}
                {isLoading && !messages.some(m => m.isStreaming) && <TypingIndicator />}
                <div ref={messagesEndRef} />
              </div>
            </div>
          ) : (
            <WelcomeScreen 
              onModeSelect={handleModeSelect}
              onCategorySelect={(cat) => setSelectedCategory(cat.name)}
              dailyPearl={dailyPearl}
            />
          )}

          {/* Input Area - Glass-morph pastel */}
          <div className="flex-shrink-0 bg-white/70 backdrop-blur-xl border-t border-white/60 p-4 shadow-[0_-8px_30px_rgb(192,18,60,0.04)]">
            <div className="max-w-3xl mx-auto">
              {/* Quick Templates */}
              {currentTemplates && currentTemplates.length > 0 && messages.length < 2 && (
                <div className="mb-4">
                  <p className="text-xs font-medium text-slate-400 mb-2">Quick templates</p>
                  <div className="flex flex-wrap gap-2">
                    {currentTemplates.slice(0, 6).map((template, idx) => (
                      <button
                        key={idx}
                        data-testid={`quick-template-${idx}`}
                        onClick={() => {
                          setInputText(template);
                          setTimeout(() => {
                            document.querySelector('[data-testid="send-btn"]')?.click();
                          }, 150);
                        }}
                        className="px-3.5 py-1.5 text-xs text-raspberry-800 bg-white/80 border border-raspberry-100 rounded-full hover:bg-raspberry-50 hover:border-raspberry-200 transition-colors shadow-sm"
                      >
                        {template}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* HIPAA Data Notice */}
              <div className="mb-2 flex items-center gap-1.5 px-1">
                <div className="w-1.5 h-1.5 rounded-full bg-green-500 flex-shrink-0" />
                <p className="text-xs text-slate-400">Sessions auto-delete in 24h. Do not enter patient-identifiable information.</p>
              </div>
              {/* File Preview */}
              {selectedFile && (
                <div className="mb-3 p-3 bg-slate-50 rounded-lg border border-slate-200 flex items-center gap-3">
                  {filePreview ? (
                    <img src={filePreview} alt="Preview" className="w-12 h-12 rounded object-cover" />
                  ) : (
                    <div className="w-12 h-12 rounded bg-slate-200 flex items-center justify-center">
                      <Paperclip className="w-5 h-5 text-slate-500" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{selectedFile.name}</p>
                    <p className="text-xs text-slate-500">{(selectedFile.size / 1024).toFixed(1)} KB</p>
                  </div>
                  <button
                    onClick={clearSelectedFile}
                    className="p-1.5 rounded hover:bg-slate-200 text-slate-500"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}

              {/* Input Row - Mobile Optimized */}
              <div className="flex items-end gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                
                {/* Mobile: + button toggles tools | Desktop: show all buttons */}
                <div className="relative sm:hidden">
                  <button
                    onClick={() => setMobileToolsOpen(!mobileToolsOpen)}
                    className={`p-3 rounded-lg border transition-colors ${mobileToolsOpen ? 'bg-slate-100 border-slate-300' : 'border-slate-200 hover:bg-slate-50'} text-slate-500`}
                    data-testid="mobile-tools-toggle"
                  >
                    <Plus className="w-5 h-5" />
                  </button>
                  {mobileToolsOpen && (
                    <div className="absolute bottom-14 left-0 bg-white border border-slate-200 rounded-xl shadow-lg p-2 flex flex-col gap-1 z-20 min-w-[160px]">
                      <button
                        onClick={() => { fileInputRef.current?.click(); setMobileToolsOpen(false); }}
                        className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg hover:bg-slate-50 text-sm text-slate-700"
                        data-testid="mobile-attach-btn"
                      >
                        <Paperclip className="w-4 h-4 text-slate-400" /> Attach File
                      </button>
                      <button
                        onClick={() => { setInputText(prev => prev ? prev : 'Generate image of '); textareaRef.current?.focus(); setMobileToolsOpen(false); }}
                        className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg hover:bg-slate-50 text-sm text-slate-700"
                        data-testid="mobile-image-btn"
                      >
                        <ImageIcon className="w-4 h-4 text-slate-400" /> Generate Image
                      </button>
                      <button
                        onClick={() => { setDrugRefOpen(true); setMobileToolsOpen(false); }}
                        className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg hover:bg-slate-50 text-sm text-slate-700"
                        data-testid="mobile-drug-btn"
                      >
                        <Pill className="w-4 h-4 text-emerald-500" /> Drug Doses
                      </button>
                    </div>
                  )}
                </div>

                {/* Mobile: dedicated Mic button (always visible — most used) */}
                <button
                  onClick={isRecording ? stopVoiceInput : startVoiceInput}
                  className={`sm:hidden p-3 rounded-lg border transition-colors ${isRecording ? 'bg-red-500 border-red-500 text-white animate-pulse' : 'border-slate-200 hover:bg-slate-50 text-slate-500'}`}
                  data-testid="mobile-mic-btn-direct"
                  title={isRecording ? 'Stop recording' : 'Voice input'}
                >
                  {isRecording ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
                </button>

                {/* Desktop buttons */}
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="hidden sm:flex p-3 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-500 transition-colors"
                  disabled={isLoading}
                  title="Attach file"
                >
                  <Paperclip className="w-5 h-5" />
                </button>

                <button
                  onClick={() => {
                    setInputText(prev => prev ? prev : 'Generate image of ');
                    textareaRef.current?.focus();
                  }}
                  className="hidden sm:flex p-3 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-500 transition-colors"
                  disabled={isLoading}
                  title="Generate medical image"
                  data-testid="image-gen-btn"
                >
                  <ImageIcon className="w-5 h-5" />
                </button>

                <button
                  onClick={() => setDrugRefOpen(true)}
                  className="hidden sm:flex p-3 rounded-lg border border-slate-200 hover:bg-slate-50 text-emerald-500 transition-colors"
                  title="Drug Dose Reference"
                  data-testid="drug-ref-btn"
                >
                  <Pill className="w-5 h-5" />
                </button>

                <div className="flex-1 relative">
                  <textarea
                    ref={textareaRef}
                    value={inputText}
                    onChange={(e) => setInputText(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Ask any clinical question..."
                    className="w-full px-4 py-3 pr-24 rounded-2xl border border-slate-200 bg-white/85 backdrop-blur-sm shadow-[0_4px_20px_-8px_rgba(15,23,42,0.12)] focus:border-raspberry-300 focus:ring-2 focus:ring-raspberry-100 resize-none text-sm transition-colors"
                    rows={1}
                    disabled={isLoading}
                    data-testid="chat-input"
                  />
                  
                  <div className="absolute right-2 bottom-2 flex items-center gap-1">
                    <Select value={language} onValueChange={setLanguage}>
                      <SelectTrigger className="h-8 w-20 text-xs border-0 bg-transparent">
                        <Globe className="w-3 h-3 mr-1" />
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <ScrollArea className="h-48">
                          {LANGUAGES.map(lang => (
                            <SelectItem key={lang} value={lang} className="text-xs">{lang}</SelectItem>
                          ))}
                        </ScrollArea>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <button
                  onClick={isRecording ? stopVoiceInput : startVoiceInput}
                  className={`hidden sm:flex p-3 rounded-lg border transition-colors ${
                    isRecording
                      ? 'bg-red-500 border-red-500 text-white'
                      : 'border-slate-200 hover:bg-slate-50 text-slate-500'
                  }`}
                >
                  {isRecording ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
                </button>

                {isLoading ? (
                  <button
                    onClick={stopGeneration}
                    className="p-3 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors shadow-md animate-pulse"
                    data-testid="stop-btn"
                    title="Stop generating"
                    aria-label="Stop generating"
                  >
                    <Square className="w-5 h-5" fill="currentColor" />
                  </button>
                ) : (
                  <button
                    onClick={sendMessage}
                    disabled={!inputText.trim() && !selectedFile}
                    className="p-3 rounded-lg bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    data-testid="send-btn"
                  >
                    <Send className="w-5 h-5" />
                  </button>
                )}
              </div>




              <p className="text-xs text-center text-slate-400 mt-3">
                AI responses are clinical decision support — always verify with your judgement.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Click outside to close mode selector or export menu */}
      {(showModeSelector || showExportMenu) && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => { setShowModeSelector(false); setShowExportMenu(false); }}
        />
      )}

      {/* Calculator Modal */}
      <CalculatorModal
        isOpen={calculatorOpen}
        onClose={() => setCalculatorOpen(false)}
        onSendToChat={(note) => {
          setInputText((prev) => (prev && prev.trim() ? `${prev.trim()}\n\n${note}` : note));
          setTimeout(() => {
            try {
              const ta = document.querySelector('textarea[data-testid="chat-input"]') || document.querySelector('textarea');
              if (ta) { ta.focus(); ta.scrollTop = ta.scrollHeight; }
            } catch { /* noop */ }
          }, 80);
          toast.success('Calculator result added to chat — review and send.');
        }}
      />
      {/* Drug Reference Modal */}
      <DrugReferenceModal isOpen={drugRefOpen} onClose={() => setDrugRefOpen(false)} />
      {/* Favourites Panel */}
      <FavouritesPanel isOpen={favouritesOpen} onClose={() => setFavouritesOpen(false)} />
      <InviteModal open={inviteOpen} onClose={() => setInviteOpen(false)} />
      {/* UpgradeModal disabled during launch (free access for early gynaecologists) */}
      <LetterheadModal
        open={letterheadOpen}
        onClose={() => {
          setLetterheadOpen(false);
          setLetterheadSet(!!getStoredLetterhead());
        }}
      />
    </div>
  );
}
